import { tz } from '@date-fns/tz';
import { format, formatDistanceToNow, isValid } from 'date-fns';

import { dayParts } from './time-zone';

/**
 * Formatting helpers.
 *
 * Centralised so that a date or an amount looks identical on every screen, and
 * so a change of convention is one edit rather than fifty (§56, §57).
 */

/** The admin is English-only today; RTL/Arabic would make this configurable. */
const LOCALE = 'en-GB';

/**
 * `Intl.NumberFormat` construction is comparatively expensive and these are
 * called once per table cell, so instances are cached per currency.
 */
const currencyFormatters = new Map<string, Intl.NumberFormat>();

function currencyFormatter(currency: string, precise: boolean): Intl.NumberFormat {
  const key = precise ? `${currency}:precise` : currency;
  let formatter = currencyFormatters.get(key);

  if (!formatter) {
    const digits = precise ? { minimumFractionDigits: 2, maximumFractionDigits: 4 } : {};
    try {
      formatter = new Intl.NumberFormat(LOCALE, {
        style: 'currency',
        currency,
        currencyDisplay: 'code',
        ...digits,
      });
    } catch {
      // The backend stores CurrencyCode as free text and some rows carry
      // non-ISO values, which makes Intl throw. Fall back to plain decimals
      // rather than crashing a table row.
      formatter = new Intl.NumberFormat(LOCALE, {
        minimumFractionDigits: 2,
        maximumFractionDigits: precise ? 4 : 2,
      });
    }
    currencyFormatters.set(key, formatter);
  }

  return formatter;
}

/**
 * Format a monetary amount.
 *
 * The frontend displays money; it does not compute it. Financial arithmetic is
 * the backend's job — doing it here in IEEE-754 floats is how 0.1 + 0.2 ends up
 * on an invoice (§56).
 *
 * `precise` shows up to 4 decimals — for intermediate pricing-engine values
 * (e.g. a price before rounding), which a 2-decimal display would misstate.
 */
export function formatCurrency(
  amount: number | null | undefined,
  currency = 'USD',
  options: { precise?: boolean } = {},
): string {
  if (amount === null || amount === undefined || Number.isNaN(amount)) return '—';
  return currencyFormatter(currency, options.precise === true).format(amount);
}

/** A fraction (0.1138…) as a percentage, at most six significant digits (11.3858%). */
export function formatPercent(fraction: number | null | undefined): string {
  if (fraction === null || fraction === undefined || Number.isNaN(fraction)) return '—';
  return new Intl.NumberFormat(LOCALE, {
    style: 'percent',
    maximumSignificantDigits: 6,
  }).format(fraction);
}

export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat(LOCALE).format(value);
}

const HAS_OFFSET = /(?:Z|[+-]\d{2}:?\d{2})$/i;

/**
 * Parse a backend instant.
 *
 * The Admin API sends every instant in UTC with an explicit offset (`…Z`). A value without one
 * breaks that contract: it is reported in development and rendered as missing, never read in
 * some guessed zone. Returns null rather than an Invalid Date so callers get one failure mode.
 */
export function parseInstant(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  if (value instanceof Date) return isValid(value) ? value : null;
  if (!HAS_OFFSET.test(value)) {
    if (import.meta.env.DEV) console.warn(`Instant without a UTC offset: "${value}"`);
    return null;
  }
  const date = new Date(value);
  return isValid(date) ? date : null;
}

/** e.g. "21 Sep 2026": the instant's day in the given market zone. */
export function formatDate(value: string | Date | null | undefined, zone: string): string {
  const date = parseInstant(value);
  return date ? format(date, 'd MMM yyyy', { in: tz(zone) }) : '—';
}

/** e.g. "21 Sep 2026, 14:32": the instant in the given market zone. */
export function formatDateTime(value: string | Date | null | undefined, zone: string): string {
  const date = parseInstant(value);
  return date ? format(date, 'd MMM yyyy, HH:mm', { in: tz(zone) }) : '—';
}

/** e.g. "about 3 hours ago": a duration, so no zone applies. */
export function formatRelativeDate(value: string | Date | null | undefined): string {
  const date = parseInstant(value);
  return date ? formatDistanceToNow(date, { addSuffix: true }) : '—';
}

/**
 * e.g. "21 Sep 2026" for a calendar day (`yyyy-MM-dd`): a settlement period, a value date, a
 * report day. A day is not an instant, so it is shown as written and never converted.
 */
export function formatDay(value: string | null | undefined, pattern = 'd MMM yyyy'): string {
  const parts = value ? dayParts(value) : null;
  if (!parts) return '—';
  return format(new Date(Date.UTC(parts.year, parts.month - 1, parts.day)), pattern, {
    in: tz('UTC'),
  });
}

/**
 * Mask a sensitive value, showing only the last few characters.
 *
 * Digital codes are the product — a leaked code is stolen money — so they are
 * masked by default everywhere and revealed only on explicit, permissioned
 * action (§52).
 *
 *   maskSecret('ABCD-EFGH-IJKL-8123') -> '••••-••••-••••-8123'
 */
export function maskSecret(value: string | null | undefined, visibleSuffix = 4): string {
  if (!value) return '—';
  if (value.length <= visibleSuffix) return '•'.repeat(value.length);

  const suffix = value.slice(-visibleSuffix);
  const masked = value.slice(0, -visibleSuffix).replace(/[^-\s]/g, '•');

  return `${masked}${suffix}`;
}
